import { ReactNode, useRef, useState } from 'react'
import { Modal } from '../components'
import { ModalContext } from '../contexts'

export type InputModalProps<T> = {
  title?: string
  description?: string
  confirmButtonText: string
  cancelButtonText: string
  onConfirm?: () => void
  onCancel?: () => void
  initialValue?: T
  Content?: ({ setValue }: { value: T | undefined; setValue: (value: T) => void }) => ReactNode
}

type ModalState = {
  open: boolean
  content?: ReactNode
} & Partial<InputModalProps<any>>

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const inputRef = useRef<any>(null)

  const [modalState, setModalState] = useState<ModalState>({ open: false })

  const showInputModal = <T,>({
    title,
    description,
    confirmButtonText,
    cancelButtonText,
    onConfirm,
    onCancel,
    initialValue,
    Content,
  }: InputModalProps<T>) => {
    return new Promise<T | undefined>((resolve, reject) => {
      inputRef.current = initialValue

      setModalState({
        open: true,
        title,
        description,
        confirmButtonText,
        cancelButtonText,
        content: Content ? (
          <Content
            value={initialValue}
            setValue={(value: T) => {
              inputRef.current = value
            }}
          />
        ) : null,
        onConfirm: () => {
          onConfirm?.()
          resolve(inputRef.current)
          hideModal()
        },
        onCancel: () => {
          onCancel?.()
          reject()
          hideModal()
        },
      })
    })
  }

  const hideModal = () => {
    setModalState((prev) => ({ ...prev, open: false }))
  }

  return (
    <ModalContext
      value={{
        showInputModal,
      }}
    >
      <Modal open={modalState.open} onOpenChange={(open) => setModalState((prev) => ({ ...prev, open }))}>
        <Modal.Content
          title={modalState.title}
          description={modalState.description}
          confirmButtonText={modalState.confirmButtonText}
          cancelButtonText={modalState.cancelButtonText}
          onConfirm={modalState.onConfirm}
          onCancel={modalState.onCancel}
        >
          {modalState.content}
        </Modal.Content>
      </Modal>
      {children}
    </ModalContext>
  )
}
